import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import { RendererType } from '../data-models/field-definition';
import { ISimpleChoice } from '../data-models/field2.data-model';
import { IDateMeta, ISelectMeta, ITextMeta } from '../data-models/field3.data-model';
import { IFinanceRoundDataModel } from '../data-models/finance-round.data-model';
import { IRoundDataModel } from '../data-models/round.data-model';
import { CompoundingPeriod, CouponType } from '../data-models/transaction.data-model';
import { roundsByCompanyIdStateJ } from '../pages/Finance2/state/FinanceStateJ';
import { roundsByIdMapAtom, transactionTypesAtom } from '../services/state/AppConfigStateJ';
import { FMT } from '../util/formatter-service';
import { formatDate } from '../util/formatters/DateFormatters';
import { getRoundLabel } from '../util/preference-utils';
import { createFormField, IFormField, IFormFieldSelectMeta } from '../view-models/form.view-model';
import { WarrantsType } from '../view-models/transaction-form.view-model';
import { FieldSettings, futureDateField } from './CommonFields';

export function useGetTransactionTypeField() {
  const getTransactionTypes = useGetTransactionTypesSelectItems();

  return useCallback(() => {
    const values = getTransactionTypes();

    return createFormField<ISelectMeta<number>>({
      key: 'transactionTypeId',
      label: 'Transaction Type',
      required: true,
      renderer: RendererType.singleSelect,
      rendererMeta: {
        multi: false,
        values,
      },
    });
  }, [getTransactionTypes]);
}

export function useGetTransactionTypesSelectItems() {
  const transactionTypes = useAtomValue(transactionTypesAtom);

  return useCallback(() => {
    const values: ISimpleChoice<number>[] = transactionTypes
      .map((transactionType) => {
        return {
          value: transactionType.id,
          displayName: FMT.format('transactionType', transactionType.id),
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    return values;
  }, [transactionTypes]);
}

export function transactionDateField() {
  return createFormField<IDateMeta>({
    key: 'transactionDate',
    label: 'Transaction Date',
    required: true,
    renderer: RendererType.date,
  });
}

export function securityField() {
  return createFormField<ITextMeta>({
    key: 'security',
    label: 'Security',
    renderer: RendererType.text,
  });
}

export function maturityDateField() {
  return futureDateField({
    key: 'maturityDate',
    label: 'Maturity Date',
    required: false,
  });
}

export function couponTypeField() {
  return createFormField<ISelectMeta<string>>({
    key: 'couponType',
    label: 'Interest Type',
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: Object.values(CouponType).map((type) => ({ displayName: type, value: type })),
    },
  });
}

export function compoundingPeriodField({ key, label = key, required = false }: FieldSettings) {
  return createFormField<ISelectMeta<string>>({
    key,
    label,
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: Object.values(CompoundingPeriod).map((period) => ({ displayName: period, value: period })),
    },
    required,
  });
}

export function getInvestmentLabel(
  investmentRound: IFinanceRoundDataModel,
  roundsById: Map<number, IRoundDataModel>
): string {
  const roundName = roundsById.get(investmentRound.roundId ?? -1)?.name ?? '';

  if (investmentRound.name) {
    return `${investmentRound.name}${roundName ? ` (${roundName})` : ''}`;
  } else if (investmentRound.roundDate) {
    return `${roundName ? `${roundName} ` : ''}(${formatDate(investmentRound.roundDate)})`;
  } else {
    return roundName || '-';
  }
}

export function useInvestmentAssignmentField(
  companyId: number,
  onCreateInvestment: (name?: string) => void
): IFormField<IFormFieldSelectMeta<number>> {
  const investmentRoundsByCompany = useAtomValue(roundsByCompanyIdStateJ(companyId));
  const roundsById = useAtomValue(roundsByIdMapAtom);

  const options: ISimpleChoice<number>[] = useMemo(() => {
    if (!investmentRoundsByCompany) {
      return [];
    }
    return investmentRoundsByCompany
      .map((r) => ({
        value: r.id,
        displayName: getInvestmentLabel(r, roundsById),
        secondaryLabel: FMT.format('dateNumeric', r.roundDate),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [investmentRoundsByCompany, roundsById]);

  const roundLabel = getRoundLabel();

  return createFormField({
    dataType: 'number',
    key: 'id',
    label: roundLabel,
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: options,
      multi: false,
      actionMeta: {
        onAction: onCreateInvestment,
        actionLabel: `Add New ${roundLabel}`,
        helperText: `Select an existing ${roundLabel.toLowerCase()} or add a new one`,
      },
    },
    disableClearable: true,
  });
}

export function getValuationImpactField({
  key = 'valuationImpact',
  label = 'Valuation Impact',
  required = false,
}: Partial<FieldSettings> = {}) {
  return createFormField<unknown>({
    key,
    label,
    required,
    renderer: RendererType.boolean,
    description:
      'By toggling on, all securities held in the company will be marked to the PPS of this transaction',
  });
}

export function warrantsTypeField() {
  return createFormField<ISelectMeta<string>>({
    key: '_viewModel.warrantsType',
    label: 'Warrants Type',
    required: true,
    renderer: RendererType.singleSelect,
    disableClearable: false,
    rendererMeta: {
      multi: false,
      values: Object.values(WarrantsType).map((type) => ({ displayName: type, value: type })),
    },
  });
}
